import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getActiveRuns, getRunSnapshot, type OnboardingRun } from '../api/onboardingRuns';
import { showSuccessMessage, showErrorMessage } from '@/shared/adapters/toast';
import { notifyAiLimit } from '@/shared/utils/apiError';
import { _t } from '@/i18n/t';
import { APP_ROUTES } from '@/app/routePaths';
import { isBuildFlowOnScreen } from '../utils/buildFlowPresence';

const POLL_INTERVAL = 8000;

export const activeBuildRunKey = ['th', 'onboarding-runs', 'active-bootstrap'] as const;

export interface FinishedBuildRun {
  runId: number;
  contentId?: number;
}

export function useActiveBuildRun(enabled: boolean, onFinished?: (run: FinishedBuildRun) => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery<OnboardingRun | null>({
    queryKey: activeBuildRunKey,
    queryFn: async () => {
      const runs = await getActiveRuns();
      return runs.find(r => r.kind === 'onboarding_bootstrap') ?? runs.find(r => r.kind === 'site_health') ?? null;
    },
    enabled,
    refetchInterval: POLL_INTERVAL,
    refetchOnWindowFocus: true,
  });

  const lastRunIdRef = useRef<number | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const { data } = query;

  useEffect(() => {
    if (data) {
      lastRunIdRef.current = data.id;
      return;
    }
    const finishedRunId = lastRunIdRef.current;
    if (finishedRunId === null) return;
    lastRunIdRef.current = null;

    void queryClient.invalidateQueries({ queryKey: ['th', 'dashboard'] });

    getRunSnapshot(finishedRunId)
      .then(({ run }) => {
        if (run.status === 'completed') {
          const contentId = run.resultPayload?.contentId as number | undefined;
          if (!isBuildFlowOnScreen()) {
            const action = contentId
              ? { label: 'View' as const, onClick: () => navigate(APP_ROUTES.contentOptimize(contentId)) }
              : undefined;
            showSuccessMessage(_t('onboarding.build.doneToast'), action);
          }
          onFinishedRef.current?.({ runId: finishedRunId, contentId });
        } else if (run.status === 'failed') {
          if (!notifyAiLimit(run.error)) showErrorMessage(run.error?.message || _t('onboarding.build.errorToast'));
        }
      })
      .catch(() => {});
  }, [data, navigate, queryClient]);

  return query;
}
